// The `/` menu: insert or turn into a block.
//
// Built on `@tiptap/suggestion` (the same as the `[[` list), positioned by its
// own floating-ui (`props.mount`).
//
// Promise:
// - `/` opens it at the start of a top-level paragraph (every item), at the start
//   of a paragraph inside a callout (lists only: that is what a callout holds),
//   and in the middle of a paragraph after something that is not a letter, a
//   digit or a URL character (only items that add to the paragraph without
//   replacing it: a footnote, a file). So `1/2`, `a/b`, `https://` and `~/` never
//   open it, and Japanese text (no spaces) still can. Never inside code, headings
//   or inline code. Whether it opens and what it lists are decided by one
//   function (`slashContextAt`), so it never opens with nothing to offer.
// - `/xxx` filters: the label contains xxx, or an English alias starts with it
//   (`/h1` and the localized label find the same item; `/e` does not list
//   everything).
// - ↑ ↓ move, Enter chooses (Tab is indentation), Escape closes. The hover moves
//   the choice only when the mouse really moved: scrolling a row into view makes
//   Chromium send a synthetic mouse event at the resting pointer, which would
//   pull the choice back under it.
// - On a phone the panel is placed in the visible part of the screen (the
//   visual viewport, above the keyboard) and shrinks to fit there; it scrolls.
// - An atom block (a diagram) is inserted with an empty paragraph after it, so
//   the selection lands in text: a selected atom would be replaced by the next
//   keystroke.
// - The file item is there only when the editor can take files
//   (`onInsertFile`: the body's upload); an editor can leave items out
//   (`exclude`: the footnote dialog has no footnotes and no files). The
//   document body adds its parts' items (`items`: the ui fence's block).
import { shift, size } from "@floating-ui/dom";
import { type Editor, Extension, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionOptions } from "@tiptap/suggestion";
import { findFootnoteDef, insertToggle } from "@trameria/editor";
import { m } from "@trameria/i18n";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { isImeComposing } from "../../ui/ime";
import { openFootnoteEditor } from "../blocks/footnote-view";
import {
  POPUP_PANEL,
  POPUP_ROW,
  raisePopup,
  SuggestEmpty,
  suggestKeyDown,
} from "./suggest";

// ---- items -----------------------------------------------------------------------

export interface SlashItem {
  id: string;
  label(): string;
  /** The markdown it stands for, shown faintly on the right. */
  hint(): string;
  /** English aliases matched by prefix. */
  keywords: readonly string[];
  run(editor: Editor, range: Range): void;
  /** Also offered inside a callout (its content takes paragraphs and lists). */
  inCallout?: true;
  /**
   * Also offered in the middle of a paragraph: only items that add to it
   * without turning the paragraph into something else.
   */
  inline?: true;
  /** Offered only when the host can pick files. */
  needsFiles?: true;
}

/** The host's way to pick and insert files (`range` covers "/" and the query). */
export type InsertFile = (editor: Editor, range: Range) => void;

const setBlock =
  (name: string, attrs: Record<string, unknown> = {}) =>
  (editor: Editor, range: Range) =>
    editor.chain().focus().deleteRange(range).setNode(name, attrs).run();

/** An atom block, then an empty paragraph to type in. */
function insertAtom(
  editor: Editor,
  range: Range,
  node: { type: string; attrs?: Record<string, unknown> },
) {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent([node, { type: "paragraph" }])
    .run();
}

/**
 * A callout with an empty title and one empty paragraph, the caret in the
 * paragraph: the title is optional, so writing starts in the body.
 */
function insertCallout(editor: Editor, range: Range) {
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent({
      type: "callout",
      attrs: { kind: "note" },
      content: [
        { type: "calloutTitle" },
        { type: "calloutContent", content: [{ type: "paragraph" }] },
      ],
    })
    .run();
  // callout(+1) → empty title(+1) → its end(+1) → content(+1) → paragraph(+1).
  const at = range.from - 1;
  if (editor.state.doc.nodeAt(at)?.type.name !== "callout") return;
  editor.commands.setTextSelection(at + 5);
}

/**
 * A footnote label not used yet. Labels are not shown (footnotes are numbered
 * in order), so a short random one does; a clash would edit another footnote's
 * text, so it is drawn again (a few times at most, never blocking).
 */
export function freshFootnoteLabel(editor: Editor): string {
  for (let i = 0; i < 10; i++) {
    const label = `fn-${Math.random().toString(36).slice(2, 7)}`;
    if (findFootnoteDef(editor.state.doc, label) === null) return label;
  }
  return `fn-${Date.now().toString(36)}`;
}

/**
 * A footnote: the reference here, then its dialog (`openFootnoteEditor` makes
 * the empty definition at the end of the document first: definitions always
 * sit at the end).
 */
function insertFootnote(editor: Editor, range: Range) {
  const label = freshFootnoteLabel(editor);
  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent({ type: "footnoteRef", attrs: { label } })
    .run();
  openFootnoteEditor(editor, label);
}

/** The items: the built-in ones, then `extra` (the document body's parts: block-parts.ts). */
export function slashItems(
  insertFile?: InsertFile,
  extra: readonly SlashItem[] = [],
): SlashItem[] {
  const items: SlashItem[] = [
    {
      id: "heading1",
      label: () => m.documents_block_heading1(),
      hint: () => "#",
      keywords: ["h1", "heading1", "heading", "midashi", "title"],
      run: setBlock("heading", { level: 1 }),
    },
    {
      id: "heading2",
      label: () => m.documents_block_heading2(),
      hint: () => "##",
      keywords: ["h2", "heading2", "heading", "midashi"],
      run: setBlock("heading", { level: 2 }),
    },
    {
      id: "heading3",
      label: () => m.documents_block_heading3(),
      hint: () => "###",
      keywords: ["h3", "heading3", "heading", "midashi"],
      run: setBlock("heading", { level: 3 }),
    },
    {
      id: "bulletList",
      label: () => m.documents_block_bullet_list(),
      hint: () => "-",
      keywords: ["ul", "list", "bullet", "bulletlist"],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleBulletList().run(),
      inCallout: true,
    },
    {
      id: "orderedList",
      label: () => m.documents_block_numbered_list(),
      hint: () => "1.",
      keywords: ["ol", "list", "number", "orderedlist"],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
      inCallout: true,
    },
    {
      id: "taskList",
      label: () => m.documents_block_task_list(),
      hint: () => "- [ ]",
      keywords: ["task", "todo", "check", "checkbox", "tasklist", "list"],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleTaskList().run(),
    },
    {
      id: "blockquote",
      label: () => m.documents_block_quote(),
      hint: () => ">",
      keywords: ["quote", "blockquote", "inyou"],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
    },
    {
      id: "codeBlock",
      label: () => m.documents_block_code(),
      hint: () => "```",
      keywords: ["code", "codeblock", "pre"],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
    },
    {
      id: "horizontalRule",
      label: () => m.documents_block_divider(),
      hint: () => "---",
      keywords: ["hr", "divider", "rule", "separator"],
      run: (editor, range) =>
        editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
    },
    {
      id: "table",
      label: () => m.documents_block_table(),
      hint: () => "| a | b |",
      keywords: ["table", "hyou", "grid"],
      run: (editor, range) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run(),
    },
    {
      id: "callout",
      label: () => m.documents_block_callout(),
      hint: () => "> [!NOTE]",
      keywords: ["callout", "note", "info", "warning", "tip"],
      run: insertCallout,
    },
    {
      id: "toggle",
      label: () => m.documents_block_toggle(),
      hint: () => "<details>",
      keywords: ["toggle", "details", "accordion", "fold"],
      run: insertToggle,
    },
    {
      id: "footnote",
      label: () => m.documents_block_footnote(),
      hint: () => "[^n]",
      keywords: ["footnote", "note", "kyakuchu"],
      run: insertFootnote,
      // A footnote reference is inline: adding one mid-line is the usual case.
      inline: true,
    },
    {
      id: "file",
      label: () => m.documents_block_file(),
      hint: () => m.documents_block_file_hint(),
      keywords: ["file", "image", "gazou", "tenpu", "attach", "upload"],
      run: (editor, range) => insertFile?.(editor, range),
      needsFiles: true,
      // A second file is added mid-line, after the first one's chip.
      inline: true,
    },
    {
      id: "mermaid",
      label: () => m.documents_block_mermaid(),
      hint: () => "```mermaid",
      keywords: ["mermaid", "diagram", "chart", "zu", "flowchart"],
      run: (editor, range) =>
        insertAtom(editor, range, {
          type: "trameriaFence",
          attrs: {
            kind: "mermaid",
            payload: m.documents_mermaid_default_payload(),
          },
        }),
    },
  ];
  return [...items, ...extra].filter(
    (item) => !item.needsFiles || insertFile !== undefined,
  );
}

/** Where a `/` sits: what decides whether the menu opens and what it lists. */
export type SlashContext = "block" | "callout" | "inline" | null;

/** After one of these, a `/` is part of a word, a path, a date or a URL. */
const URLISH_CHAR = /[A-Za-z0-9:/._~%?#=&+@\\-]/;

type StateLike = Editor["state"];

export function slashContextAt(state: StateLike, from: number): SlashContext {
  // The `/` typed inside inline code takes the code mark: it is code.
  if (state.doc.nodeAt(from)?.marks.some((x) => x.type.name === "code"))
    return null;
  const $from = state.doc.resolve(from);
  if ($from.parent.type.name !== "paragraph") return null;
  const inCallout =
    $from.depth >= 2 &&
    $from.node($from.depth - 1).type.name === "calloutContent";
  if ($from.parentOffset === 0 && ($from.depth === 1 || inCallout))
    return inCallout ? "callout" : "block";
  const before = $from.nodeBefore;
  // The start of a paragraph inside a list item or a quote, or after a chip.
  if (!before?.isText) return "inline";
  const text = before.text ?? "";
  return URLISH_CHAR.test(text.charAt(text.length - 1)) ? null : "inline";
}

export function filterSlashItems(
  items: readonly SlashItem[],
  query: string,
  context: SlashContext,
): SlashItem[] {
  if (context === null) return [];
  const pool =
    context === "callout"
      ? items.filter((i) => i.inCallout)
      : context === "inline"
        ? items.filter((i) => i.inline)
        : items;
  const q = query.trim().toLowerCase();
  if (q === "") return [...pool];
  return pool.filter(
    (i) =>
      i.label().toLowerCase().includes(q) ||
      i.keywords.some((k) => k.startsWith(q)),
  );
}

// ---- the panel -------------------------------------------------------------------

const MAX_HEIGHT = 288;
/** Three rows: below that the menu has flipped to the other side already. */
const MIN_HEIGHT = 96;
const EDGE = 8;

/**
 * Fixed positioning measures against the visual viewport: on a phone the
 * keyboard shrinks only that, and an absolute panel was placed behind the
 * keyboard. `size` fits the panel into the room left (after the plugin's flip);
 * `shift` keeps it inside the screen sideways.
 */
const FLOATING = {
  strategy: "fixed" as const,
  middleware: [
    shift({ padding: EDGE }),
    size({
      padding: EDGE,
      apply({ availableHeight, elements }) {
        const h = Math.max(
          MIN_HEIGHT,
          Math.min(MAX_HEIGHT, Math.floor(availableHeight)),
        );
        // The floating element is the renderer's box (a column flex), the panel
        // inside shrinks to it.
        elements.floating.style.maxHeight = `${h}px`;
      },
    }),
  ],
};

interface ListProps {
  items: SlashItem[];
  command(item: SlashItem): void;
}

export interface SlashListHandle {
  onKeyDown(props: { event: KeyboardEvent }): boolean;
}

export const SlashMenuList = forwardRef<SlashListHandle, ListProps>(
  function SlashMenuList({ items, command }, ref) {
    const [selected, setSelected] = useState(0);
    const rows = useRef<(HTMLButtonElement | null)[]>([]);
    const lastPointer = useRef<{ x: number; y: number } | null>(null);

    // frontend-development: not a fetch; a new query starts at the first item.
    // biome-ignore lint/correctness/useExhaustiveDependencies: the change of items is the trigger
    useEffect(() => {
      setSelected(0);
    }, [items]);
    // frontend-development: not a fetch; keeps the chosen row in view.
    // biome-ignore lint/correctness/useExhaustiveDependencies: items too (the first row after filtering)
    useEffect(() => {
      rows.current[selected]?.scrollIntoView?.({ block: "nearest" });
    }, [selected, items]);

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown({ event }) {
          if (isImeComposing(event)) return false;
          return suggestKeyDown(event, {
            items,
            selected,
            setSelected,
            command,
            confirmKeys: ["Enter"],
          });
        },
      }),
      [items, selected, command],
    );

    return (
      <div
        className={`${POPUP_PANEL} min-h-0 w-60`}
        style={{ maxHeight: MAX_HEIGHT }}
        role="listbox"
        aria-label={m.documents_block_insert()}
      >
        {items.length === 0 ? (
          <SuggestEmpty />
        ) : (
          items.map((item, index) => (
            <button
              key={item.id}
              ref={(el) => {
                rows.current[index] = el;
              }}
              type="button"
              role="option"
              aria-selected={index === selected}
              tabIndex={-1}
              // On mousedown: by click time the editor's blur has closed the menu.
              onMouseDown={(e) => {
                e.preventDefault();
                command(item);
              }}
              onMouseMove={(e) => {
                const last = lastPointer.current;
                if (last && last.x === e.clientX && last.y === e.clientY)
                  return;
                lastPointer.current = { x: e.clientX, y: e.clientY };
                setSelected(index);
              }}
              className={`${POPUP_ROW} text-body ${index === selected ? "bg-hover" : ""}`}
            >
              <span className="pointer-events-none min-w-0 flex-1 truncate">
                {item.label()}
              </span>
              <span className="pointer-events-none flex-none font-mono text-[11px] text-faint">
                {item.hint()}
              </span>
            </button>
          ))
        )}
      </div>
    );
  },
);

// ---- the extension -----------------------------------------------------------------

/** Its own key: two suggestion plugins with one key make ProseMirror throw. */
export const slashMenuKey = new PluginKey("trameriaSlashMenu");

export interface SlashMenuOptions {
  /** Picks files and inserts them; without it there is no file item. */
  onInsertFile?: InsertFile;
  /** Item ids this editor does not offer (the footnote dialog: footnotes, files). */
  exclude: readonly string[];
  /** Items added after the built-in ones (the document body's parts). */
  items: readonly SlashItem[];
  suggestion: Omit<SuggestionOptions<SlashItem>, "editor">;
}

export const SlashMenu = Extension.create<SlashMenuOptions>({
  name: "trameriaSlashMenu",

  addOptions() {
    return {
      onInsertFile: undefined,
      exclude: [],
      items: [],
      suggestion: {
        char: "/",
        pluginKey: slashMenuKey,
        // Where it may open is decided by `allow` alone (a chip has no text, so
        // a pattern would see the start of a line after it). Japanese has no
        // spaces, so no prefix is required.
        startOfLine: false,
        allowedPrefixes: null,
        allow: ({ state, range }) => slashContextAt(state, range.from) !== null,
        floatingUi: FLOATING,
        command: ({ editor, range, props }) => props.run(editor, range),
        render: () => {
          let component: ReactRenderer<SlashListHandle, ListProps> | null =
            null;
          let unmount: (() => void) | null = null;
          return {
            onStart: (props) => {
              component = new ReactRenderer(SlashMenuList, {
                props: { items: props.items, command: props.command },
                editor: props.editor,
                // A column flex, so the max-height `size` writes here shrinks the panel.
                className: "flex flex-col",
              });
              unmount = props.mount(raisePopup(component.element));
            },
            onUpdate: (props) =>
              component?.updateProps({
                items: props.items,
                command: props.command,
              }),
            onKeyDown: (props) => component?.ref?.onKeyDown(props) ?? false,
            onExit: () => {
              unmount?.();
              unmount = null;
              component?.destroy();
              component = null;
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    const excluded = new Set(this.options.exclude);
    const all = slashItems(
      this.options.onInsertFile,
      this.options.items,
    ).filter((item) => !excluded.has(item.id));
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        items: ({ query, editor }) =>
          filterSlashItems(
            all,
            query,
            // Where the `/` is: the selection is after the query and the `/`.
            slashContextAt(
              editor.state,
              editor.state.selection.from - query.length - 1,
            ),
          ),
      }),
    ];
  },
});
